import React, { useMemo, useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';

const CONCURRENCY = 2;
const BATCH_DELAY_MS = 2000;
const MAX_RETRIES = 2;
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function KeywordDiscoverBulk() {
  const [input, setInput] = useState('');
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState([]); // [{seedKeyword, ideas:[{text,avgMonthlySearches,competition,...,monthly}]}]
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [expandedSeeds, setExpandedSeeds] = useState(new Set());
  const abortRef = useRef(false);

  const brandId = useMemo(() => {
    try { return localStorage.getItem('currentBrandId') || ''; } catch { return ''; }
  }, []);

  const seedKeywords = useMemo(() => {
    return input.split('\n').map(l => l.trim()).filter(Boolean);
  }, [input]);

  const discoverOne = useCallback(async (seedKeyword) => {
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const res = await fetch('https://api.glowytics.com/api/keyword-discover/suggestions', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) },
          body: JSON.stringify({
            seedKeywords: [seedKeyword],
            languageConstant: 'languageConstants/1037',
            geoTargetConstant: 'geoTargetConstants/2792',
            maxIdeas: 100,
          })
        });
        if (!res.ok) {
          if (attempt < MAX_RETRIES && (res.status === 429 || res.status >= 500)) {
            await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
            continue;
          }
          const err = await res.json().catch(() => ({}));
          return { seedKeyword, ideas: [], error: err?.error || res.statusText };
        }
        const data = await res.json();
        if (data.success === false && attempt < MAX_RETRIES) {
          await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
          continue;
        }
        return {
          seedKeyword,
          ideas: Array.isArray(data.ideas) ? data.ideas : [],
        };
      } catch (e) {
        if (attempt < MAX_RETRIES) {
          await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
          continue;
        }
        return { seedKeyword, ideas: [], error: e.message };
      }
    }
  }, [brandId]);

  const run = async () => {
    if (seedKeywords.length === 0) return;
    abortRef.current = false;
    setRunning(true);
    setResults([]);
    setExpandedSeeds(new Set());
    setProgress({ done: 0, total: seedKeywords.length });

    const allResults = [];
    let idx = 0;

    const runBatch = async () => {
      const batch = [];
      while (batch.length < CONCURRENCY && idx < seedKeywords.length) {
        if (abortRef.current) break;
        batch.push(discoverOne(seedKeywords[idx]));
        idx++;
      }
      const batchResults = await Promise.all(batch);
      allResults.push(...batchResults);
      setResults(prev => [...prev, ...batchResults]);
      setProgress({ done: allResults.length, total: seedKeywords.length });
    };

    while (idx < seedKeywords.length && !abortRef.current) {
      await runBatch();
      if (idx < seedKeywords.length && !abortRef.current) {
        await new Promise(r => setTimeout(r, BATCH_DELAY_MS));
      }
    }

    setRunning(false);
  };

  const stop = () => { abortRef.current = true; };

  const toggleSeed = (seed) => {
    setExpandedSeeds(prev => {
      const next = new Set(prev);
      if (next.has(seed)) next.delete(seed); else next.add(seed);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedSeeds(new Set(results.map(r => r.seedKeyword)));
  };
  const collapseAll = () => setExpandedSeeds(new Set());

  // Helpers
  const getAvg = (monthly) => {
    if (!monthly || monthly.length === 0) return 0;
    const sum = monthly.reduce((s, m) => s + Number(m.monthly_searches || 0), 0);
    return Math.round(sum / monthly.length);
  };

  const getLast12Total = (monthly) => {
    if (!monthly || monthly.length === 0) return 0;
    const sorted = [...monthly].sort((a, b) => (a.year * 100 + a.month) - (b.year * 100 + b.month));
    return sorted.slice(-12).reduce((s, m) => s + Number(m.monthly_searches || 0), 0);
  };

  const get3MonthChange = (monthly) => {
    if (!monthly || monthly.length < 6) return null;
    const sorted = [...monthly].sort((a, b) => (a.year * 100 + a.month) - (b.year * 100 + b.month));
    const last3 = sorted.slice(-3);
    const prev3 = sorted.slice(-6, -3);
    const sumLast = last3.reduce((s, m) => s + Number(m.monthly_searches || 0), 0);
    const sumPrev = prev3.reduce((s, m) => s + Number(m.monthly_searches || 0), 0);
    if (sumPrev === 0) return null;
    return Math.round(((sumLast - sumPrev) / sumPrev) * 100);
  };

  const getYoyChange = (monthly) => {
    if (!monthly || monthly.length < 24) return null;
    const sorted = [...monthly].sort((a, b) => (a.year * 100 + a.month) - (b.year * 100 + b.month));
    const last12 = sorted.slice(-12);
    const prev12 = sorted.slice(-24, -12);
    const sumLast = last12.reduce((s, m) => s + Number(m.monthly_searches || 0), 0);
    const sumPrev = prev12.reduce((s, m) => s + Number(m.monthly_searches || 0), 0);
    if (sumPrev === 0) return null;
    return Math.round(((sumLast - sumPrev) / sumPrev) * 100);
  };

  const formatPct = (val) => {
    if (val === null || val === undefined) return '-';
    const color = val > 0 ? 'text-emerald-600' : val < 0 ? 'text-red-600' : 'text-gray-500';
    return <span className={color}>{val > 0 ? '+' : ''}{val}%</span>;
  };

  const competitionColor = (comp) => {
    if (!comp || comp === 'UNKNOWN' || comp === 'UNSPECIFIED') return 'bg-gray-50 text-gray-600 border-gray-200';
    if (comp === 'LOW') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (comp === 'MEDIUM') return 'bg-amber-50 text-amber-700 border-amber-200';
    if (comp === 'HIGH') return 'bg-red-50 text-red-700 border-red-200';
    return 'bg-gray-50 text-gray-600 border-gray-200';
  };

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return '-';
    return `${val.toFixed(2)} ₺`;
  };

  // Stats
  const totalIdeas = results.reduce((s, r) => s + (r.ideas?.length || 0), 0);

  // Build month columns for Excel
  const monthColumns = useMemo(() => {
    const cols = new Set();
    for (const r of results) {
      for (const idea of (r.ideas || [])) {
        for (const m of (idea.monthly || [])) {
          cols.add(`${m.year}-${String(m.month).padStart(2, '0')}`);
        }
      }
    }
    return Array.from(cols).sort();
  }, [results]);

  const exportExcel = () => {
    const rows = [];
    for (const r of results) {
      if (!r.ideas || r.ideas.length === 0) {
        rows.push({
          'Seed Keyword': r.seedKeyword,
          'Discovered Keyword': '',
          'Avg Monthly': '',
          'Last 12mo Total': '',
          '3mo Change %': '',
          'YoY Change %': '',
          'Competition': r.error || 'No ideas',
          'Competition Index': '',
          'Bid Low': '',
          'Bid High': '',
        });
        continue;
      }
      for (const idea of r.ideas) {
        const row = {
          'Seed Keyword': r.seedKeyword,
          'Discovered Keyword': idea.text,
          'Avg Monthly': idea.avgMonthlySearches || getAvg(idea.monthly),
          'Last 12mo Total': getLast12Total(idea.monthly),
          '3mo Change %': get3MonthChange(idea.monthly) ?? '',
          'YoY Change %': getYoyChange(idea.monthly) ?? '',
          'Competition': idea.competition || '',
          'Competition Index': idea.competitionIndex ?? '',
          'Bid Low': idea.top_of_page_bid_low ?? '',
          'Bid High': idea.top_of_page_bid_high ?? '',
        };
        for (const col of monthColumns) {
          const [y, m] = col.split('-').map(Number);
          const found = (idea.monthly || []).find(x => x.year === y && x.month === m);
          row[`${MONTH_NAMES[m - 1]} ${y}`] = found ? Number(found.monthly_searches || 0) : '';
        }
        rows.push(row);
      }
    }

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Keyword Discover');
    XLSX.writeFile(wb, `keyword_discover_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">LAB</span>
          <h2 className="text-xl font-bold text-gray-900">Bulk Keyword Discover</h2>
        </div>
        <p className="text-sm text-gray-500">
          Paste seed keywords (one per line). For each seed, discover related keywords with search volumes, competition and bid metrics. Export everything to Excel.
        </p>
      </div>

      {/* Input */}
      <div className="bg-white rounded-xl border shadow-sm p-5 mb-5">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">Seed Keywords ({seedKeywords.length})</label>
          <div className="flex items-center gap-2">
            {results.length > 0 && (
              <button
                onClick={exportExcel}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700"
              >
                Export Excel ({totalIdeas} ideas)
              </button>
            )}
            {running ? (
              <button
                onClick={stop}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-600 text-white hover:bg-red-700"
              >
                Stop
              </button>
            ) : (
              <button
                onClick={run}
                disabled={seedKeywords.length === 0}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Discover Keywords
              </button>
            )}
          </div>
        </div>
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          rows={6}
          placeholder={"spor ayakkabı\nkoşu ayakkabısı\nyürüyüş ayakkabısı\n..."}
          className="w-full px-3 py-2 border rounded-lg text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
          disabled={running}
        />
      </div>

      {/* Progress */}
      {(running || progress.done > 0) && (
        <div className="bg-white rounded-xl border shadow-sm p-5 mb-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-700 font-medium">
              Progress: {progress.done} / {progress.total} seeds ({pct}%) &mdash; {totalIdeas} ideas found
            </span>
            {running && <span className="text-xs text-blue-600 animate-pulse">Discovering...</span>}
            {!running && progress.done > 0 && <span className="text-xs text-emerald-600">Done</span>}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="h-2 rounded-full transition-all duration-300"
              style={{ width: `${pct}%`, background: running ? '#3b82f6' : '#10b981' }}
            />
          </div>
          {results.filter(r => r.error).length > 0 && (
            <div className="mt-2 text-xs text-red-600">
              {results.filter(r => r.error).length} seeds failed
            </div>
          )}
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="bg-white rounded-xl border shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-800">
              Results: {results.length} seeds &rarr; {totalIdeas} discovered keywords
            </h3>
            <div className="flex items-center gap-2">
              <button onClick={expandAll} className="px-2 py-1 rounded text-[10px] font-medium text-gray-600 hover:bg-gray-100">
                Expand All
              </button>
              <button onClick={collapseAll} className="px-2 py-1 rounded text-[10px] font-medium text-gray-600 hover:bg-gray-100">
                Collapse All
              </button>
              <button
                onClick={exportExcel}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700"
              >
                Export Excel
              </button>
            </div>
          </div>

          <div className="space-y-1">
            {results.map((r, ri) => {
              const isExpanded = expandedSeeds.has(r.seedKeyword);
              return (
                <div key={ri} className="border rounded-lg">
                  {/* Seed header row */}
                  <button
                    onClick={() => toggleSeed(r.seedKeyword)}
                    className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 text-left"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 text-xs">{isExpanded ? '▼' : '▶'}</span>
                      <span className="font-semibold text-sm text-gray-900">{r.seedKeyword}</span>
                      {r.error ? (
                        <span className="px-2 py-0.5 rounded text-[10px] bg-red-50 text-red-600 border border-red-200" title={r.error}>Error</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-[10px] bg-blue-50 text-blue-600 border border-blue-200">
                          {r.ideas?.length || 0} ideas
                        </span>
                      )}
                    </div>
                  </button>

                  {/* Ideas table */}
                  {isExpanded && r.ideas && r.ideas.length > 0 && (
                    <div className="overflow-x-auto border-t">
                      <table className="min-w-full text-xs">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-semibold text-gray-600">#</th>
                            <th className="px-3 py-2 text-left font-semibold text-gray-600">Keyword</th>
                            <th className="px-3 py-2 text-right font-semibold text-gray-600">Avg Monthly</th>
                            <th className="px-3 py-2 text-right font-semibold text-gray-600">Last 12mo</th>
                            <th className="px-3 py-2 text-right font-semibold text-gray-600">3mo Chg</th>
                            <th className="px-3 py-2 text-right font-semibold text-gray-600">YoY Chg</th>
                            <th className="px-3 py-2 text-center font-semibold text-gray-600">Competition</th>
                            <th className="px-3 py-2 text-right font-semibold text-gray-600">Bid Low</th>
                            <th className="px-3 py-2 text-right font-semibold text-gray-600">Bid High</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.ideas.map((idea, ii) => (
                            <tr key={ii} className="border-t hover:bg-gray-50">
                              <td className="px-3 py-1.5 text-gray-400">{ii + 1}</td>
                              <td className="px-3 py-1.5 font-medium text-gray-900 max-w-[300px] truncate">{idea.text}</td>
                              <td className="px-3 py-1.5 text-right font-mono">
                                {(idea.avgMonthlySearches || getAvg(idea.monthly)).toLocaleString('tr-TR')}
                              </td>
                              <td className="px-3 py-1.5 text-right font-mono">
                                {getLast12Total(idea.monthly).toLocaleString('tr-TR')}
                              </td>
                              <td className="px-3 py-1.5 text-right font-mono">{formatPct(get3MonthChange(idea.monthly))}</td>
                              <td className="px-3 py-1.5 text-right font-mono">{formatPct(getYoyChange(idea.monthly))}</td>
                              <td className="px-3 py-1.5 text-center">
                                {idea.competition && idea.competition !== 'UNKNOWN' && idea.competition !== 'UNSPECIFIED' ? (
                                  <span className={`px-2 py-0.5 rounded text-[10px] border ${competitionColor(idea.competition)}`}>
                                    {idea.competition}
                                  </span>
                                ) : '-'}
                              </td>
                              <td className="px-3 py-1.5 text-right font-mono text-gray-600">{formatCurrency(idea.top_of_page_bid_low)}</td>
                              <td className="px-3 py-1.5 text-right font-mono text-gray-600">{formatCurrency(idea.top_of_page_bid_high)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {isExpanded && r.ideas && r.ideas.length === 0 && !r.error && (
                    <div className="px-4 py-3 border-t text-xs text-gray-500">No ideas returned for this seed.</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
